// src/pages/admin/IntegrationSettings.jsx
// Admin-only platform integration credentials, stored in admin_settings.
// Currently: TikTok Shop Partner app (key 'tiktok_shop') — used by
// api/fetch-tiktok-shop.js getShopAppConfig() for the merchant OAuth flow.
// Without this row every "Connect TikTok Shop" attempt fails with
// "credentials not configured".
//
// Reads/writes go through /api/admin-settings (server-side, admin-gated)
// rather than the browser Supabase client — app_secret is a real secret,
// and admin_settings' RLS policy is live-only in Supabase (not in the
// repo), so this endpoint is the actual security boundary, not RLS. The
// secret is never sent back to the browser after saving; the form only
// ever shows whether it's "configured".
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { authFetch } from '../../lib/authFetch';
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent,
  Button, Input,
} from '../../components/ui';
import { Loader2, Save, ShoppingBag, ExternalLink, Filter, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';

export default function IntegrationSettings() {
  const [form, setForm] = useState({ app_key: '', app_secret: '', service_id: '' });
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [templateCount, setTemplateCount] = useState(null);

  const loadTemplateCount = async () => {
    try {
      const { count } = await supabase
        .from('funnel_templates')
        .select('*', { count: 'exact', head: true })
        .eq('is_system', true);
      setTemplateCount(count ?? 0);
    } catch {
      setTemplateCount(null);
    }
  };
  useEffect(() => { loadTemplateCount(); }, []);

  const handleSyncTemplates = async () => {
    setSyncing(true);
    try {
      const res = await authFetch('/api/funnels?resource=templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'sync_templates' }),
      });
      const data = await res.json();
      if (!data.success) {
        toast.error(data.error || 'Sync failed');
        return;
      }
      toast.success(`Synced ${data.synced} funnel templates`);
      loadTemplateCount();
    } catch (e) {
      toast.error('Sync failed: ' + e.message);
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch('/api/admin-settings?key=tiktok_shop');
        const data = await res.json();
        if (!data.success) {
          toast.error(data.error || 'Failed to load settings');
          return;
        }
        // app_secret is deliberately never returned — see file header.
        setForm({ app_key: data.value.app_key || '', app_secret: '', service_id: data.value.service_id || '' });
        setConfigured(data.configured);
      } catch (e) {
        console.error('Integration settings load error:', e);
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    if (!form.app_key.trim() || (!form.app_secret.trim() && !configured)) {
      toast.error('App Key and App Secret are required');
      return;
    }
    setSaving(true);
    try {
      const res = await authFetch('/api/admin-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'save',
          key: 'tiktok_shop',
          value: {
            app_key: form.app_key.trim(),
            app_secret: form.app_secret.trim(), // blank = keep existing (server-side)
            service_id: form.service_id.trim(),
          },
        }),
      });
      const data = await res.json();
      if (!data.success) {
        toast.error(data.error || 'Failed to save');
        return;
      }
      setForm(f => ({ ...f, app_secret: '' }));
      setConfigured(data.configured);
      toast.success('TikTok Shop credentials saved — users can now connect their shops');
    } catch (e) {
      console.error('Save error:', e);
      toast.error('Failed to save: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShoppingBag className="h-4 w-4" /> TikTok Shop Partner App
            {configured
              ? <span className="text-xs font-medium text-green-600 bg-green-500/10 rounded-full px-2 py-0.5">Configured</span>
              : <span className="text-xs font-medium text-amber-600 bg-amber-500/10 rounded-full px-2 py-0.5">Not configured</span>}
          </CardTitle>
          <CardDescription>
            App-level credentials for the TikTok Shop merchant OAuth flow. Every user's
            "Connect TikTok Shop" button fails until these are set.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1.5 block">App Key</label>
            <Input
              value={form.app_key}
              onChange={e => setForm(f => ({ ...f, app_key: e.target.value }))}
              placeholder="6a8b..."
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">App Secret</label>
            <Input
              type="password"
              value={form.app_secret}
              onChange={e => setForm(f => ({ ...f, app_secret: e.target.value }))}
              placeholder={configured ? 'Configured — leave blank to keep it' : '••••••••••••'}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Never shown once saved. Leave blank when editing other fields to keep the current secret.
            </p>
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Service ID (optional)</label>
            <Input
              value={form.service_id}
              onChange={e => setForm(f => ({ ...f, service_id: e.target.value }))}
              placeholder="Defaults to App Key if empty"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Used in the Partner authorize URL (services.tiktokshop.com/open/authorize?service_id=…).
            </p>
          </div>

          <div className="rounded-lg bg-muted/60 px-4 py-3 text-xs text-muted-foreground space-y-1">
            <p className="font-medium text-foreground">Where to find these</p>
            <p>
              Create/open your app in the{' '}
              <a href="https://partner.tiktokshop.com" target="_blank" rel="noopener noreferrer"
                className="text-primary hover:underline inline-flex items-center gap-0.5">
                TikTok Shop Partner Center <ExternalLink className="h-3 w-3" />
              </a>{' '}
              under App &amp; Service → App Management. Set the app's redirect URL to{' '}
              <code className="bg-background rounded px-1">https://app.revhapi.com/tiktok-shop</code>.
            </p>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Credentials
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Filter className="h-4 w-4" /> Funnel Template Library
            {templateCount !== null && (
              <span className="text-xs font-medium text-muted-foreground bg-muted rounded-full px-2 py-0.5">
                {templateCount} seeded
              </span>
            )}
          </CardTitle>
          <CardDescription>
            Upserts the system funnel templates from the repo (lib/funnel-templates) into the
            template gallery. Run once after deploy, and again whenever templates change.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-end">
            <Button onClick={handleSyncTemplates} disabled={syncing} variant="outline">
              {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Sync Templates
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
